/* global runtime */
// Lønnskjøringer, lønnsslipper, godkjenning, betaling og innsending.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const savePayrollRun = async (run = {}) => {
  const id = run.id || createId('payroll-run');
  const savedRun = stripUndefinedDeep({
    ...run,
    id,
    period: run.period || todayIso().slice(0, 7),
    paymentDate: nbToIso(run.paymentDate || run.paymentDateIso || todayIso()),
    status: run.status || 'draft',
    lines: safeArray(run.lines),
    createdAt: run.createdAt || nowIso(),
  });
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'replace',
    runId: id,
    run: savedRun,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run || savedRun;
};
const createPayrollRun = async (options = {}) => {
  const state = mergeState(runtime.appState);
  const run = buildPayrollRunFromState(state, options);
  if (!run) throw new Error(t('svc.noLinesToCreate'));
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'replace',
    runId: run.id,
    run,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run || run;
};
const createPayrollRunCorrection = async (runId, { period, paymentDate, reason } = {}) => {
  const correctionRunId = createId('payroll-correction');
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'create_correction',
    runId,
    metadata: { correctionRunId, period, paymentDate: nbToIso(paymentDate), reason },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run;
};

const updatePayrollRunById = async (runId, updater) => {
  if (!runId) throw new Error(t('svc.missingPayrollRun'));
  const state = mergeState(runtime.appState);
  const current = safeArray(state.payrollRuns).find((run) => run.id === runId);
  if (!current) throw new Error(t('svc.payrollRunNotFound'));
  const savedRun = stripUndefinedDeep(updater(current, state));
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'replace',
    runId,
    run: savedRun,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run || savedRun;
};

const makePayrollPostingSnapshot = (run) => buildPayrollPostingRows({ ...run, status: run.status === 'draft' ? 'approved' : run.status })
  .map((posting, index) => stripUndefinedDeep({
    id: `${run.id || 'payroll'}-posting-${index + 1}`,
    ...posting,
  }));

const getPayrollEmployeeForPayslip = (state, employeeId) =>
  safeArray(state.employees).find((employee) => employee.id === employeeId) || null;

const getPayrollPayslipRecipients = (state, run, payslips = {}) => {
  const selected = payslips.selected || {};
  const emails = payslips.emails || {};
  const recipients = safeArray(run.lines)
    .filter((line) => selected[line.employeeId] !== false)
    .map((line) => {
      const employee = getPayrollEmployeeForPayslip(state, line.employeeId) || {};
      const email = String(emails[line.employeeId] || employee.email || employee.personalia?.epost || '').trim();
      return stripUndefinedDeep({
        employeeId: line.employeeId,
        employeeName: line.employeeName || employee.name || t('pay.employee'),
        email,
      });
    });
  if (!recipients.length) throw new Error(t('svc.pickPayslipEmployee'));
  const missingEmail = recipients.find((recipient) => !recipient.email);
  if (missingEmail) throw new Error(t('svc.payslipMissingEmail', { name: missingEmail.employeeName }));
  return recipients;
};

const buildPayrollPayslipSelection = (lines, employeeIds = []) => {
  const requested = new Set(safeArray(employeeIds).map((employeeId) => String(employeeId || '')).filter(Boolean));
  return Object.fromEntries(safeArray(lines).map((line) =>
    [line.employeeId, requested.size === 0 || requested.has(String(line.employeeId || ''))]));
};

const sendPayrollRunPayslipEmails = async (state, run, payslips = {}) => {
  const recipients = getPayrollPayslipRecipients(state, run, payslips);
  const sendNow = payslips.when !== 'date';
  const sendAt = sendNow ? nowIso() : nbToIso(payslips.sendDate || todayIso());
  const results = [];
  const requestId = createId('payslip-delivery');
  for (const recipient of recipients) {
    if (!sendNow) {
      results.push({ ...recipient, status: 'scheduled', sendAt });
      continue;
    }
    const payload = await authorizedPost('sendPayslipEmailHttp', {
      requestId,
      employee: { id: recipient.employeeId, email: recipient.email },
      payrollRun: { id: run.id },
      settings: { locale: PlummeI18n.getLocale(), email: state.settings?.email || runtime.auth?.currentUser?.email || '' },
      businessId: runtime.activeBusinessId || undefined,
    });
    results.push({ ...recipient, ...payload.recipient, status: payload.status, messageId: payload.messageId });
  }
  return results;
};

const downloadPayrollPayslipPdf = async (runId, employeeId, { download = true } = {}) => {
  const ok = await initFirebase();
  if (!ok || !runtime.db || !runtime.auth?.currentUser) {
    throw new Error(t('svc.backendNotConfigured'));
  }
  const state = mergeState(runtime.appState);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  if (!run) throw new Error(t('svc.payrollNotFound'));
  const line = safeArray(run.lines).find((entry) => entry.employeeId === employeeId);
  if (!line) throw new Error(t('svc.employeeLineNotFound'));
  const employee = getPayrollEmployeeForPayslip(state, employeeId) || { id: employeeId, name: line.employeeName || t('pay.employee') };
  const settings = stripUndefinedDeep({
    ...(state.settings || {}),
    locale: PlummeI18n.getLocale(),
    businessName: state.settings?.businessName || buildCompany(state).name,
    email: state.settings?.email || runtime.auth?.currentUser?.email || '',
  });
  const payload = await authorizedPost('downloadPayslipPdfHttp', {
    employee: stripUndefinedDeep({ ...employee, id: employeeId, name: line.employeeName || employee.name || t('pay.employee') }),
    payrollRun: run,
    payrollRuns: safeArray(state.payrollRuns),
    settings,
    businessId: runtime.activeBusinessId || undefined,
  });
  if (!payload?.pdfBase64) throw new Error(t('svc.noPayslipFromPdf'));
  if (download) downloadBlob(pdfBase64ToBlob(payload.pdfBase64), payload.fileName || `lonnsslipp-${run.period || run.id || employeeId}.pdf`);
  return payload;
};

const sendPayrollRunPayslips = async (runId, employeeIds = []) => {
  const state = mergeState(runtime.appState);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  if (!run) throw new Error(t('svc.payrollRunNotFound'));
  if (!['approved', 'paid'].includes(run.status)) throw new Error(t('pay.payslipsAfterPosting'));
  const results = await sendPayrollRunPayslipEmails(state, run, {
    selected: buildPayrollPayslipSelection(run.lines, employeeIds),
    when: 'now',
  });
  // The enqueue transaction already records queued recipients; the worker owns sent/failed status.
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return results;
};

const approvePayrollRun = async (runId, options = {}) => {
  let pendingPayslips = null;
  const approvalState = mergeState(runtime.appState);
  const approvalRun = safeArray(approvalState.payrollRuns).find((entry) => entry.id === runId);
  // The server enforces this too; checking here gives the employee and rule by name.
  assertPayrollRunTaxRulesSupported(approvalState, approvalRun);
  // A travel or expense claim is paid through one run only (H9). The server re-validates.
  const travelIssues = window.PlummePayrollTravelClaims.payabilityIssues(approvalState, approvalRun);
  if (travelIssues.length) throw new Error(t('pay.travelNotPayableError', { travels: travelIssues.map((issue) => [issue.employeeName, issue.description].filter(Boolean).join(': ') || issue.travelId).join(', ') }));
  if (options.payslips) {
    if (!approvalRun) throw new Error(t('svc.payrollRunNotFound'));
    getPayrollPayslipRecipients(approvalState, approvalRun, options.payslips);
    pendingPayslips = options.payslips;
  }
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'approve',
    runId,
    metadata: { payslipStatus: 'generated' },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  if (!pendingPayslips) return result.run;
  try {
    const approvedState = mergeState(runtime.appState);
    const approvedRun = safeArray(approvedState.payrollRuns).find((run) => run.id === runId) || result.run;
    const results = await sendPayrollRunPayslipEmails(approvedState, approvedRun, { ...pendingPayslips, when: 'now' });
    window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
    emitRuntime();
    return { ...approvedRun, payslipStatus: 'queued', payslipRecipients: results };
  } catch (error) {
    return { ...result.run, _warning: t('pay.approvedButPayslipsFailed', { error: error?.message || t('svc.unknownError') }) };
  }
};

const markPayrollRunManualSubmission = async (runId) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'mark_manual_submission', runId,
    metadata: { confirmed: true },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run;
};

const submitPayrollRunAltinn = async () => {
  throw new Error(t('svc.ameldingNotConfigured'));
};

const fetchPayrollNavRefunds = async () => {
  throw new Error(t('svc.navNotConfigured'));
};

const resetPayrollRunForEditing = async (runId) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'reset', runId,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run;
};

const annulPayrollRunAccounting = async (runId) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'annul_accounting', runId,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result.run;
};

const markPayrollRunPaid = async (runId, paidAt = todayIso(), details = {}) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'mark_paid', runId,
    metadata: { paidAt: nbToIso(paidAt), settlementAccountCode: details.settlementAccountCode || '1920', employeeIds: safeArray(details.employeeIds) },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result.run;
};

const savePayrollTaxPayment = async ({ year, term, payment = {}, replacePaymentId = '' } = {}) => {
  const paymentYear = Number(year) || Number(runtime.appState?.activeYear) || new Date().getFullYear();
  const paymentTerm = Number(term);
  if (payment.kind !== 'withholding' && !paymentTerm) throw new Error(t('svc.missingPeriod'));
  const state = mergeState(runtime.appState);
    const accountId = String(payment.accountId || '').trim();
    const account = safeArray(state.settings?.bankAccounts).find((entry) => entry.id === accountId);
    const settlementAccountCode = payment.settlementAccountCode || (account ? getLedgerAccountCode(account, '1920') : '1920');
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'save_tax_payment',
    metadata: { year: paymentYear, term: paymentTerm || undefined, reportingYear: payment.reportingYear, replacePaymentId, payment: { ...payment, accountId, settlementAccountCode, date: nbToIso(payment.date || todayIso()) } },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.payment;
};

const annulPayrollTaxPayment = async ({ year, paymentId } = {}) => {
  const paymentYear = Number(year) || Number(runtime.appState?.activeYear) || new Date().getFullYear();
  if (!paymentId) throw new Error(t('svc.missingPayment'));
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'annul_tax_payment',
    metadata: { year: paymentYear, paymentId },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result.payment;
};

const markPayrollRunPensionPaid = async (runId, paidAt = todayIso(), details = {}) => {
  const state = mergeState(runtime.appState);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  if (!run) throw new Error(t('svc.payrollRunNotFound'));
  const pensionSettings = { ...(state.payrollSettings || {}), country: state.settings?.country, effectiveDate: paidAt || run.paymentDate };
  const pensionAmount = safeArray(run.lines).reduce((sum, line) => sum + payrollLinePensionContribution(line, pensionSettings), 0);
  if (pensionAmount <= 0) throw new Error(t('svc.noOtpCalculated'));
  const accountId = String(details.accountId || '').trim();
  const account = safeArray(state.settings?.bankAccounts).find((entry) => entry.id === accountId);
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'mark_pension_paid',
    runId,
    metadata: {
      paidAt: nbToIso(paidAt),
      invoiceDate: nbToIso(details.invoiceDate || paidAt),
      dueDate: details.dueDate ? nbToIso(details.dueDate) : undefined,
      invoiceNumber: String(details.invoiceNumber || '').trim() || undefined,
      amount: num(details.amount, pensionAmount),
      premiumAmount: num(details.premiumAmount, pensionAmount),
      employeeContributionAmount: num(details.employeeContributionAmount),
      feeAmount: num(details.feeAmount),
      feeVatRate: num(details.feeVatRate),
      feeAccountCode: details.feeAccountCode || '7770',
      overpaymentAccountCode: details.overpaymentAccountCode || '1570',
      paymentId: details.paymentId || `pension-payment-${runId}-${safeArray(run.pensionCorrectionHistory).length}`,
      settlementAccountCode: details.settlementAccountCode || (account ? getLedgerAccountCode(account, '1920') : '1920'),
    },
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result.run;
};

const changePayrollPensionPayment = async (runId, details, annul = false) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, runId,
    action: annul ? 'annul_pension_payment' : 'correct_pension_payment', metadata: details,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime(); window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result.run;
};

const pensionInvoiceMutation = async (action, metadata) => {
  const result = await authorizedPost('payrollRunMutationHttp', { businessId: runtime.activeBusinessId, action, metadata });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime(); window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result;
};
